import React, { useState } from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Snackbar,
  Stepper,
  Step,
  StepLabel,
  Stack,
  FormHelperText,
  Fade,
  Zoom,
  Tooltip,
  Avatar,
  InputAdornment,
  LinearProgress,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  ContentCopy as CopyIcon,
  Visibility as PreviewIcon,
  Save as SaveIcon,
  CheckCircle as CheckCircleIcon,
  FormatListNumbered as ListIcon,
  Psychology as PsychologyIcon,
  Score as ScoreIcon,
  Info as InfoIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import { getLabelByKey, useChangeableItems } from "store/useDynamicLabel";

const Add = () => {
  // Questions state
  const [questions, setQuestions] = useState([
    {
      id: Date.now(),
      questionText: "",
      questionType: "mcq",
      marks: 1,
      options: ["", "", "", ""],
      correctAnswer: "",
      difficulty: "medium",
      explanation: "",
    },
  ]);

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const changeableItems = useChangeableItems();
  const getLabel = (itemKey) => getLabelByKey(changeableItems, itemKey);

  // Add new question with animation support
  const addQuestion = () => {
    const newQuestion = {
      id: Date.now(),
      questionText: "",
      questionType: "mcq",
      marks: 1,
      options: ["", "", "", ""],
      correctAnswer: "",
      difficulty: "medium",
      explanation: "",
    };
    setQuestions([...questions, newQuestion]);
  };

  // Duplicate question
  const duplicateQuestion = (id) => {
    const questionToDuplicate = questions.find((q) => q.id === id);
    if (questionToDuplicate) {
      const duplicatedQuestion = {
        ...questionToDuplicate,
        id: Date.now(),
        questionText: `${questionToDuplicate.questionText} (Copy)`,
      };
      setQuestions([...questions, duplicatedQuestion]);
      showSnackbar("Question duplicated successfully", "success");
    }
  };

  // Delete question
  const deleteQuestion = (id) => {
    if (questions.length > 1) {
      setQuestions(questions.filter((q) => q.id !== id));
      showSnackbar("Question deleted successfully", "info");
    } else {
      showSnackbar("At least one question is required", "warning");
    }
  };

  // Update question field
  const updateQuestion = (id, field, value) => {
    setQuestions(
      questions.map((q) => (q.id === id ? { ...q, [field]: value } : q)),
    );
  };

  // Update option text
  const updateOption = (questionId, optionIndex, value) => {
    setQuestions(
      questions.map((q) => {
        if (q.id === questionId) {
          const newOptions = [...q.options];
          newOptions[optionIndex] = value;
          return { ...q, options: newOptions };
        }
        return q;
      }),
    );
  };

  // Add option for MCQ
  const addOption = (questionId) => {
    setQuestions(
      questions.map((q) => {
        if (q.id === questionId) {
          return { ...q, options: [...q.options, ""] };
        }
        return q;
      }),
    );
  };

  // Remove option from MCQ
  const removeOption = (questionId, optionIndex) => {
    setQuestions(
      questions.map((q) => {
        if (q.id === questionId && q.options.length > 2) {
          const newOptions = [...q.options];
          newOptions.splice(optionIndex, 1);
          return { ...q, options: newOptions };
        }
        return q;
      }),
    );
  };

  // Calculate statistics
  const calculateStats = () => {
    const totalMarks = questions.reduce(
      (sum, q) => sum + (parseInt(q.marks) || 0),
      0,
    );
    const easyCount = questions.filter((q) => q.difficulty === "easy").length;
    const mediumCount = questions.filter(
      (q) => q.difficulty === "medium",
    ).length;
    const hardCount = questions.filter((q) => q.difficulty === "hard").length;
    const mcqCount = questions.filter((q) => q.questionType === "mcq").length;
    const tfCount = questions.filter(
      (q) => q.questionType === "truefalse",
    ).length;
    const descCount = questions.filter(
      (q) => q.questionType === "descriptive",
    ).length;
    const questionTypeOptions = ["MCQ", "Descriptive", "Fill in the blanks", "Circuit Lab"];

    return {
      totalMarks,
      easyCount,
      mediumCount,
      hardCount,
      mcqCount,
      tfCount,
      descCount,
    };
  };

  const stats = calculateStats();

  // Build payload for backend
  const buildPayload = () => {
    const payload = {
      questions: questions.map((q) => {
        const baseQuestion = {
          id: q.id,
          questionText: q.questionText.trim(),
          questionType: q.questionType,
          marks: parseInt(q.marks) || 0,
          difficulty: q.difficulty,
          explanation: q.explanation?.trim() || "",
        };

        if (q.questionType === "mcq") {
          return {
            ...baseQuestion,
            options: q.options.filter((opt) => opt.trim() !== ""),
            correctAnswer: q.correctAnswer,
          };
        } else if (q.questionType === "truefalse") {
          return {
            ...baseQuestion,
            correctAnswer: q.correctAnswer === "true",
          };
        } else {
          return baseQuestion;
        }
      }),
      totalQuestions: questions.length,
      totalMarks: stats.totalMarks,
      createdAt: new Date().toISOString(),
      statistics: {
        difficulty: stats,
        typeDistribution: {
          mcq: stats.mcqCount,
          truefalse: stats.tfCount,
          descriptive: stats.descCount,
        },
      },
    };

    return payload;
  };

  // Validate and submit
  const handleSubmit = () => {
    // Validate questions
    for (const q of questions) {
      if (!q.questionText.trim()) {
        showSnackbar(
          `Question ${questions.indexOf(q) + 1}: Please enter question text`,
          "error",
        );
        return;
      }

      if (q.marks <= 0) {
        showSnackbar(
          `Question ${questions.indexOf(q) + 1}: Marks must be greater than 0`,
          "error",
        );
        return;
      }

      if (q.questionType === "mcq") {
        const validOptions = q.options.filter((opt) => opt.trim() !== "");
        if (validOptions.length < 2) {
          showSnackbar(
            `Question ${questions.indexOf(q) + 1
            }: At least 2 options required for MCQ`,
            "error",
          );
          return;
        }
        if (!q.correctAnswer) {
          showSnackbar(
            `Question ${questions.indexOf(q) + 1
            }: Please select correct answer for MCQ`,
            "error",
          );
          return;
        }
      }
    }

    const payload = buildPayload();
    console.log("Question Paper Payload:", JSON.stringify(payload, null, 2));

    showSnackbar(
      "Question paper created successfully! Payload logged to console.",
      "success",
    );
  };

  // Show snackbar notification
  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Difficulty chip colors
  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case "easy":
        return "success";
      case "medium":
        return "warning";
      case "hard":
        return "error";
      default:
        return "default";
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", p: 2 }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold">
          <Stack direction="row" alignItems="center" spacing={1}>
            <PsychologyIcon color="primary" />
            <span>{`Create ${getLabel('QUESTION_PAPER')}`}</span>
          </Stack>
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Design professional assessments with multiple question types and
          comprehensive analytics
        </Typography>
      </Box>

      {/* Progress Stats */}
      <Fade in>
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            mb: 4,
            bgcolor: "primary.light",
            background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
            borderRadius: 2,
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <Stack direction="row" spacing={3} flexWrap="wrap">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Avatar
                    sx={{ bgcolor: "primary.main", width: 32, height: 32 }}
                  >
                    <ListIcon fontSize="small" />
                  </Avatar>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Total Questions
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {questions.length}
                    </Typography>
                  </Box>
                </Stack>

                <Stack direction="row" alignItems="center" spacing={1}>
                  <Avatar
                    sx={{ bgcolor: "secondary.main", width: 32, height: 32 }}
                  >
                    <ScoreIcon fontSize="small" />
                  </Avatar>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Total Marks
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {stats.totalMarks}
                    </Typography>
                  </Box>
                </Stack>
              </Stack>
            </Grid>
          </Grid>
        </Paper>
      </Fade>

      {/* Main Content Grid */}
      <Box container spacing={3} fullWidth>
        {/* Left Column - Instructions and Questions */}
        <Grid item xs={12} lg={8}>
          {/* Questions List */}
          <AnimatePresence>
            {questions.map((question, index) => (
              <motion.div
                key={question.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <Card elevation={1} sx={{ mb: 3, borderRadius: 2 }}>
                  <CardContent sx={{ p: 3 }}>
                    {/* Question Header */}
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={2}
                      mb={3}
                    >
                      <Avatar
                        sx={{ bgcolor: "primary.main", width: 36, height: 36 }}
                      >
                        {index + 1}
                      </Avatar>
                      <Box flex={1}>
                        <Typography variant="h6" fontWeight="bold">
                          Question {index + 1}
                        </Typography>
                      </Box>

                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => deleteQuestion(question.id)}
                            disabled={questions.length === 1}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Stack>

                    {/* Question Content */}
                    <Grid>
                      {/* Question Text */}
                      <Grid>
                        <Typography variant="subtitle2" fontWeight="bold">
                          Write Question
                        </Typography>
                        <TextField
                          fullWidth
                          multiline
                          rows={2}
                          // label="Question Text"
                          value={question.questionText}
                          onChange={(e) =>
                            updateQuestion(
                              question.id,
                              "questionText",
                              e.target.value,
                            )
                          }
                          placeholder="Enter your question here..."
                          variant="outlined"
                          sx={{ mb: 2 }}
                        />
                      </Grid>

                      {/* Question Configuration */}
                      <Grid item xs={12} mb={2}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="subtitle2" fontWeight="bold">
                              Type
                            </Typography>
                            <FormControl fullWidth size="small">
                              <Select
                                value={question.questionType}
                                // label="Type"
                                onChange={(e) =>
                                  updateQuestion(
                                    question.id,
                                    "questionType",
                                    e.target.value,
                                  )
                                }
                              >
                                <MenuItem value="mcq">MCQ</MenuItem>
                                <MenuItem value="truefalse">
                                  True/False
                                </MenuItem>
                                <MenuItem value="descriptive">
                                  Descriptive
                                </MenuItem>
                                <MenuItem value="circuitlab">
                                  Circuit Lab
                                </MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>

                          <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="subtitle2" fontWeight="bold">
                              Marks
                            </Typography>
                            <TextField
                              fullWidth
                              type="number"
                              value={question.marks}
                              onChange={(e) =>
                                updateQuestion(
                                  question.id,
                                  "marks",
                                  e.target.value,
                                )
                              }
                              size="small"
                              InputProps={{ inputProps: { min: 1 } }}
                            />
                          </Grid>
                        </Grid>
                      </Grid>

                      {/* MCQ Options */}
                      {question.questionType === "mcq" && (
                        <Grid item xs={12}>
                          <Paper
                            variant="outlined"
                            sx={{ p: 2, bgcolor: "grey.50" }}
                          >
                            <Stack
                              direction="row"
                              alignItems="center"
                              justifyContent="space-between"
                              mb={2}
                            >
                              <Typography variant="subtitle2" fontWeight="bold">
                                Multiple Choice Options
                              </Typography>
                              <Button
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={() => addOption(question.id)}
                                disabled={question.options.length >= 6}
                              >
                                Add Option
                              </Button>
                            </Stack>

                            <Grid container spacing={2}>
                              {question.options.map((option, optIndex) => (
                                <Grid item xs={12} md={6} key={optIndex}>
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                  >
                                    <Chip
                                      label={String.fromCharCode(65 + optIndex)}
                                      size="small"
                                      color={
                                        question.correctAnswer ===
                                          `option${optIndex}`
                                          ? "success"
                                          : "default"
                                      }
                                    />
                                    <TextField
                                      fullWidth
                                      size="small"
                                      placeholder={`Option ${String.fromCharCode(
                                        65 + optIndex,
                                      )}`}
                                      value={option}
                                      onChange={(e) =>
                                        updateOption(
                                          question.id,
                                          optIndex,
                                          e.target.value,
                                        )
                                      }
                                    />
                                    <Tooltip title="Mark as Correct">
                                      <IconButton
                                        size="small"
                                        color={
                                          question.correctAnswer ===
                                            `option${optIndex}`
                                            ? "success"
                                            : "default"
                                        }
                                        onClick={() =>
                                          updateQuestion(
                                            question.id,
                                            "correctAnswer",
                                            `option${optIndex}`,
                                          )
                                        }
                                      >
                                        <CheckCircleIcon />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Remove">
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          removeOption(question.id, optIndex)
                                        }
                                        disabled={question.options.length <= 2}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Stack>
                                </Grid>
                              ))}
                            </Grid>
                          </Paper>
                        </Grid>
                      )}

                      {/* True/False Options */}
                      {question.questionType === "truefalse" && (
                        <Grid item xs={12}>
                          <Paper
                            variant="outlined"
                            sx={{ p: 2, bgcolor: "grey.50" }}
                          >
                            <Typography
                              variant="subtitle2"
                              fontWeight="bold"
                              mb={2}
                            >
                              Select Correct Answer
                            </Typography>
                            <Stack direction="row" spacing={2}>
                              <Button
                                fullWidth
                                variant={
                                  question.correctAnswer === "true"
                                    ? "contained"
                                    : "outlined"
                                }
                                color={
                                  question.correctAnswer === "true"
                                    ? "success"
                                    : "primary"
                                }
                                onClick={() =>
                                  updateQuestion(
                                    question.id,
                                    "correctAnswer",
                                    "true",
                                  )
                                }
                                size="large"
                              >
                                ✓ True
                              </Button>
                              <Button
                                fullWidth
                                variant={
                                  question.correctAnswer === "false"
                                    ? "contained"
                                    : "outlined"
                                }
                                color={
                                  question.correctAnswer === "false"
                                    ? "error"
                                    : "primary"
                                }
                                onClick={() =>
                                  updateQuestion(
                                    question.id,
                                    "correctAnswer",
                                    "false",
                                  )
                                }
                                size="large"
                              >
                                ✗ False
                              </Button>
                            </Stack>
                          </Paper>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </AnimatePresence>
          <Grid
            container
            justifyContent="flex-end"
            alignItems="stretch"
            spacing={2}
            sx={{ mt: 2 }}
          >
            {/* Add Question Button */}
            <Grid item>
              <motion.div
                style={{ height: "100%" }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addQuestion}
                  size="medium"
                  sx={{
                    height: "100%", // 🔥 same height
                    minWidth: 220,
                    py: 1.8,
                    borderRadius: 2,
                    borderStyle: "dashed",
                    borderWidth: 2,
                    borderColor: "primary.light",
                  }}
                >
                  Add New Question
                </Button>
              </motion.div>
            </Grid>

            {/* Save Button */}
            <Grid item>
              <motion.div
                style={{ height: "100%" }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  onClick={handleSubmit}
                  size="medium"
                  sx={{
                    height: "100%", // 🔥 same height
                    minWidth: 220,
                    py: 1.8,
                    borderRadius: 2,
                  }}
                >
                  Save
                </Button>
              </motion.div>
            </Grid>
          </Grid>
        </Grid>
      </Box>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
          elevation={6}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Add;
